import { useEffect, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import {
  clearSignalRConnectionId,
  setSignalRConnectionId,
} from "./signalRConnectionStore";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const SIGNALR_NEGOTIATE_PATH = "/planning/signalr/negotiate";
const SIGNALR_GROUP_JOIN_PATH = "/planning/signalr/groups/join";
const MAX_GROUP_JOIN_RETRIES = 5;
const MAX_RETRY_DELAY_MS = 30000;


export type SignalRNotification = {
  topic: string;
  entityId: string;
  entityName: string;
  buCode: string;
  buType: string;
};

export type SignalREventHandler = (notification: SignalRNotification) => void;
export type SignalRHandlers = Record<string, SignalREventHandler>;

export type BuCodeRefLike = {
  current: string;
};

type SignalRConnectionInfo = {
  url: string;
  accessToken: string;
};

async function negotiateConnection(clientName: string): Promise<SignalRConnectionInfo> {
  if (!API_BASE_URL) {
    throw new Error("REACT_APP_API_BASE_URL is not configured");
  }

  const negotiateUrlWithClient = `${API_BASE_URL}${SIGNALR_NEGOTIATE_PATH}?clientName=${encodeURIComponent(clientName)}`;

  const executeNegotiate = async (url: string): Promise<SignalRConnectionInfo> => {
    const response = await fetch(url, {
      method: "POST",
      mode: "cors",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`SignalR negotiate failed with status ${response.status}`);
    }

    const connectionInfo = await response.json();
    console.log("[SignalR] Negotiate successful", {
      status: response.status,
      negotiateUrl: url,
      hasConnectionUrl: Boolean(connectionInfo?.url),
      hasAccessToken: Boolean(connectionInfo?.accessToken),
    });

    return connectionInfo;
  };

  try {
    return await executeNegotiate(negotiateUrlWithClient);
  } catch (error) {
    console.warn("[SignalR] Negotiate with clientName failed, retrying without query", error);
    return executeNegotiate(`${API_BASE_URL}${SIGNALR_NEGOTIATE_PATH}`);
  }
}

async function joinGroupWithConnection(
  buCode: string,
  clientType: string,
  connectionId: string,
): Promise<void> {
  if (!API_BASE_URL) {
    throw new Error("REACT_APP_API_BASE_URL is not configured");
  }

  if (!connectionId) {
    throw new Error("SignalR connectionId is required to join group");
  }

  const response = await fetch(`${API_BASE_URL}${SIGNALR_GROUP_JOIN_PATH}`, {
    method: "POST",
    mode: "cors",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      buCode,
      clientType,
      connectionId,
    }),
  });

  if (!response.ok) {
    throw new Error(`SignalR group join failed with status ${response.status}`);
  }

  if (response.status === 204) {
    console.log(`[SignalR] Group join successful (204) for bu:${buCode}`, {
      clientType,
      connectionId,
    });
    return;
  }

  console.log(`[SignalR] Group join successful for bu:${buCode}`, {
    status: response.status,
    clientType,
    connectionId,
  });
}

export function useSignalR(
  buCodeRef: BuCodeRefLike,
  clientName: string,
  handlers: SignalRHandlers,
): void {
  const handlersRef = useRef<SignalRHandlers>(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    let isDisposed = false;
    let connection: signalR.HubConnection | null = null;

    const registerHandlers = (hubConnection: signalR.HubConnection): void => {
      const registeredEventNames = Object.keys(handlersRef.current);
      registeredEventNames.forEach((eventName) => {
        hubConnection.on(eventName, (notification: SignalRNotification) => {
          console.log(
            `[SignalR] Notification received: topic=${notification.topic} entityId=${notification.entityId} entityName=${notification.entityName} buCode=${notification.buCode} buType=${notification.buType}`,
          );
          const handler = handlersRef.current[eventName];
          if (handler) {
            handler(notification);
          }
        });
      });
      console.log(`[SignalR] Registered handlers for events:`, registeredEventNames);
    };

    const joinGroupWithRetry = async (
      groupBuCode: string,
      currentConnectionId: string,
    ): Promise<void> => {
      for (let attempt = 1; attempt <= MAX_GROUP_JOIN_RETRIES; attempt += 1) {
        try {
          await joinGroupWithConnection(groupBuCode, clientName, currentConnectionId);
          return;
        } catch (error) {
          const delayMs = Math.min(2 ** attempt * 1000, MAX_RETRY_DELAY_MS);
          console.error(
            `[SignalR] Group join attempt ${attempt}/${MAX_GROUP_JOIN_RETRIES} failed`,
            error,
          );

          if (attempt === MAX_GROUP_JOIN_RETRIES) {
            throw error;
          }

          await new Promise((resolve) => {
            window.setTimeout(resolve, delayMs);
          });
        }
      }
    };

    const setupConnection = async (): Promise<void> => {
      try {
        const connectionInfo = await negotiateConnection(clientName);

        if (isDisposed) {
          return;
        }

        connection = new signalR.HubConnectionBuilder()
          .withUrl(connectionInfo.url, {
            accessTokenFactory: () => connectionInfo.accessToken,
          })
          .withAutomaticReconnect()
          .configureLogging(signalR.LogLevel.Information)
          .build();

        registerHandlers(connection);

        connection.onreconnecting((error) => {
          clearSignalRConnectionId();
          console.warn("[SignalR] Reconnecting", error);
        });

        connection.onreconnected(async () => {
          if (!connection || isDisposed) {
            return;
          }

          const reconnectedId = connection.connectionId || "";
          setSignalRConnectionId(reconnectedId);
          console.log(`[SignalR] Reconnected with connection ID ${reconnectedId}`);

          const latestBuCode = buCodeRef.current;
          if (!latestBuCode) {
            return;
          }

          try {
            await joinGroupWithRetry(latestBuCode, reconnectedId);
          } catch (joinError) {
            console.error("[SignalR] Failed to re-join group after reconnect", joinError);
          }
        });

        connection.onclose((error) => {
          clearSignalRConnectionId();
          console.warn("[SignalR] Connection closed", error);
        });

        await connection.start();

        if (isDisposed || !connection) {
          return;
        }

        const initialConnectionId = connection.connectionId || "";
        setSignalRConnectionId(initialConnectionId);
        console.log(`[SignalR] Connected with connection ID ${initialConnectionId}`);

        const initialBuCode = buCodeRef.current;
        if (initialBuCode) {
          await joinGroupWithRetry(initialBuCode, initialConnectionId);
        }
      } catch (error) {
        console.error("[SignalR] Setup failed", error);
      }
    };

    setupConnection();

    return () => {
      isDisposed = true;
      clearSignalRConnectionId();

      if (connection) {
        connection
          .stop()
          .then(() => {
            console.log("[SignalR] Connection stopped");
          })
          .catch((error) => {
            console.error("[SignalR] Error while stopping connection", error);
          });
      }
    };
  }, []);
}
